import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, MapPin, Layers, Search, UploadCloud, ArrowRight } from "lucide-react";

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }
function timeAgo(iso) {
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function AudienceSearch() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audience/batches");
        setBatches(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => batches.filter((b) => {
    if (kindFilter !== "all" && (b.kind || "bulk") !== kindFilter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (b.label || "").toLowerCase().includes(s)
        || (b.filename || "").toLowerCase().includes(s)
        || (b.coords || []).some(c => (c.label || "").toLowerCase().includes(s));
    }
    return true;
  }), [batches, search, kindFilter]);

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow="Module / 01"
        title="Audiences"
        description="Every processed audience query — single coordinates and bulk uploads — surfaces here as a card. Click into one for the full radius × time-window matrix and a live map of the points."
        testid="audiences-header"
        actions={
          <button
            data-testid="new-query-button"
            onClick={() => navigate("/upload")}
            className="bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#D93500] transition-colors"
          >
            <UploadCloud size={14} /> New audience query
          </button>
        }
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input
            data-testid="audiences-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by label, filename, or coordinate"
            className="w-full bg-white border border-[#E0E0D8] rounded-none pl-9 pr-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <select
          data-testid="audiences-kind-filter"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
        >
          <option value="all">All types</option>
          <option value="single">Single query</option>
          <option value="bulk">Bulk upload</option>
        </select>
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#555] self-center ml-auto">
          {filtered.length} of {batches.length} audiences
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#E0E0D8] bg-white p-16 text-center">
          <Layers size={28} className="mx-auto text-[#888]" strokeWidth={1.25} />
          <p className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-[#555]">
            {batches.length === 0 ? "No audiences yet" : "No audiences match these filters"}
          </p>
          {batches.length === 0 && (
            <button
              onClick={() => navigate("/upload")}
              className="inline-block mt-5 bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D93500]"
            >
              Run your first query
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="audience-cards">
          {filtered.map((b) => {
            const kind = b.kind || "bulk";
            const title = b.label || b.filename || `Query · ${b.id.slice(0, 6)}`;
            return (
              <button
                key={b.id}
                data-testid={`audience-card-${b.id}`}
                onClick={() => navigate(`/audiences/${b.id}`)}
                className="text-left bg-white border border-[#E0E0D8] hover:border-[#0A0A0A] transition-colors p-5 flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border ${
                    kind === "single"
                      ? "text-[#0052FF] border-[#0052FF]/30 bg-[#0052FF]/5"
                      : "text-[#FF3E00] border-[#FF3E00]/30 bg-[#FF3E00]/5"
                  }`}>
                    {kind === "single" ? "Single" : "Bulk"}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#888]">
                    {timeAgo(b.created_at)}
                  </span>
                </div>

                <div>
                  <p className="text-base font-semibold leading-snug line-clamp-2">{title}</p>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-[#555] mt-1 flex items-center gap-1.5">
                    <MapPin size={11} /> {b.points} point{b.points === 1 ? "" : "s"}
                    {b.failed > 0 && <span className="text-[#D91919] ml-2">· {b.failed} failed</span>}
                  </p>
                </div>

                <div className="mt-auto border-t border-[#E0E0D8] pt-3">
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#555]">
                    Audience · 1km · 30d
                  </p>
                  <div className="flex items-baseline justify-between mt-1">
                    <p className="font-mono text-2xl tracking-tighter">{fmt(b.highlight_count)}</p>
                    <span className="text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] flex items-center gap-1">
                      View <ArrowRight size={11} />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
